<template>
  <div
    class="app-select__wrapper"
    :class="wrapperStylesComputed"
  >
    <div
      v-if="hint"
      class="app-select__hint"
    >
      <small>{{ hint }}</small>
    </div>

    <div class="app-select">
      <div
        :id="id"
        tabindex="0"
        role="listbox"
        :name="name"
        class="app-select__header"
        :class="[headerStyles, headerStylesComputedBorder]"
        :aria-label="value"
        @click="toggleItemsVisibility"
        @keypress.enter="toggleItemsVisibility"
        @focus="handleFocus"
        @blur="handleBlur"
      >
        <label
          v-if="label"
          :for="name"
          class="app-select__label"
          :class="labelStylesComputed"
        >
          <span>{{ label }}</span>
        </label>
        <div :class="labelSelectedValueStylesComputed">
          <span class="capitalize mt-5">{{ value }}</span>
          <font-awesome-icon
            size="lg"
            :icon="isMenuOpen ? 'angle-up' : 'angle-down'"
          />
        </div>
      </div>
      <div
        class="app-select__card"
        :class="cardStyles"
      >
        <ul class="w-full">
          <li
            v-for="(item, index) in items"
            :key="index"
            role="option"
            :value="propertyLabel ? item[propertyLabel] : item"
            :aria-value="propertyLabel ? item[propertyLabel] : item"
            :aria-selected="propertyValue ? (item[propertyValue] === value) : item === value"
            class="app-select__item"
            :class="itemStyle(item)"
            tabindex="0"
            @click="selectItem(item)"
            @keypress.enter="selectItem(item)"
          >
            {{ propertyLabel ? item[propertyLabel] : item }}
          </li>
        </ul>
      </div>

      <AppBackdrop
        v-if="isMenuOpen"
        transparent
        @click="isMenuOpen = false"
      />
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="app-select__error"
    >
      <FontAwesomeIcon
        class="app-select__error-icon mr-5"
        icon="circle-exclamation"
      />
      <small>{{ error }}</small>
    </div>
  </div>
</template>

<script>
import AppBackdrop from '@/components/common/AppBackdrop'
import theme from '@/mixins/theme'

export default {
  // Name
  name: 'AppSelect',

  // Components
  components: {
    AppBackdrop
  },

  // Mixins
  mixins: [theme],

  // Props
  props: {
    name: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    propertyLabel: {
      type: String,
      default: ''
    },
    propertyValue: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    closeOnClick: {
      type: Boolean,
      default: true
    }
  },

  // Data
  data () {
    return {
      focused: false,
      isMenuOpen: false
    }
  },

  // Computed
  computed: {
    labelStylesComputed () {
      return {
        'app-select__label--floating': this.value
      }
    },
    labelSelectedValueStylesComputed () {
      return {
        'flex justify-between w-full mt-10': this.value
      }
    },
    wrapperStylesComputed () {
      return {
        'app-select__wrapper--floating': this.value
      }
    },
    headerStyles () {
      return {
        active: this.isMenuOpen,
        'app-select__header--floating': this.value,
        'app-select__header--error': this.error
      }
    },
    headerStylesComputedBorder () {
      if (this.focused) return this.borderColorClass(400)
      return 'border-purple-100'
    },
    cardStyles () {
      return {
        hidden: !this.isMenuOpen
      }
    }
  },

  // Methods
  methods: {
    /**
     * Handle Focus
     * sets 'focused' to true
     * @emits focus
     */
    handleFocus () {
      this.$emit('focus')
      this.focused = true
    },

    /**
     * Handle Blur
     * sets 'focused' to false
     * @emits blur
     */
    handleBlur () {
      this.$emit('blur')
      this.focused = false
    },

    /**
     * Handle the select value
     * @emits input
     */
    selectItem (item) {
      this.$emit('input', item)
      if (this.closeOnClick) {
        this.isMenuOpen = false
      }
    },

    /**
     * Toggles\s the visibility of the dropdown
     */
    toggleItemsVisibility () {
      this.isMenuOpen = !this.isMenuOpen
    },

    itemStyle (item) {
      if ((this.propertyValue && item[this.propertyValue] === this.value) || item === this.value) {
        return `active ${this.bgColorClass(100)}`
      }
      return `${this.bgColorClass(400, 'hover')} ${this.bgColorClass(400, 'focus')}`
    }
  }
}
</script>

<style scoped lang="scss">
.app-select {
  @apply relative block m-auto w-full;

  @screen sm {
    @apply max-w-500;
  }

  @screen lg {
    @apply max-w-400;
  }

  &__hint {
    @apply text-gray-400 pointer-events-none select-none;
  }

  &__wrapper {
    @apply w-full m-auto mb-15 sm:mb-30 lg:max-w-400 sm:max-w-500;
  }

  &__label {
    @apply left-[25px] text-gray-500 pointer-events-none truncate;

    transform-origin: 0 0;
    transform: translate3d(0, 0, 0);
    transition: transform 175ms ease-out;
  }

  &__label--floating {
    @apply w-full absolute;

    transform: translate3d(5px, -12.5px, 0) scale(0.75);
  }

  &__header {
    @apply cursor-pointer flex flex-row flex-wrap w-full justify-between px-30 p-15 rounded-30 outline-none;
    @apply border-2 bg-white bg-opacity-95 whitespace-nowrap text-black-500;

    &.active {
      @apply rounded-b-0 z-30;

      &:focus {
        outline: none;
      }
    }
  }

  &__header--floating {
    @apply pb-5;
  }

  &__header--error {
    @apply border-red-400;
  }

  &__card {
    @apply w-full top-full text-gray-500 text-base z-30 absolute rounded-b-30;
    @apply p-15 max-h-400 overflow-y-auto rounded-b-30 bg-white;
    @apply border-2 border-t-0 border-purple-100;
  }

  &__item {
    @apply py-5 px-15 cursor-pointer rounded-14 w-full capitalize mb-5;
    @apply duration-300 outline-none;

    &:hover:not(.active),
    &:focus:not(.active) {
      @apply bg-opacity-10;
    }

    &.active {
      @apply text-gray-400 font-semibold tracking-tight;
    }
  }

  &__error {
    @apply text-red-400;
  }
}
</style>
